import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Building2,
  ChevronDown,
  Globe,
  Lightbulb,
  Rocket,
  Scale,
  Shield,
  ShoppingCart,
  ThumbsUp,
  TrendingUp,
  Users,
  Wallet,
  Workflow,
} from "lucide-react";
import { blogEmpresarialHeroImage } from "../../../data/blogEmpresarialImages";
import { getBlogEmpresarialBundle } from "../../../data/blogEmpresarialTranslations";
import { useEmpresasLanguage } from "../../../context/EmpresasLanguageContext";
import { resolveArticleCover } from "../../../utils/articleCoverImage";

const BASE_PATH = "/empresas/blog";

const ICONS = {
  briefcase: Briefcase,
  globe: Globe,
  "shopping-cart": ShoppingCart,
  rocket: Rocket,
  wallet: Wallet,
  building: Building2,
  shield: Shield,
  workflow: Workflow,
  users: Users,
  lightbulb: Lightbulb,
  scale: Scale,
  "trending-up": TrendingUp,
};

function categoryImage(slug, categories) {
  return categories[slug]?.cardImage ?? blogEmpresarialHeroImage;
}

function ArticleCoverImg({ article, categories, className = "" }) {
  const cover = resolveArticleCover(article, categories);
  const src = cover.src || categoryImage(article?.category, categories);
  return <img src={src} alt={cover.alt} className={className} loading="lazy" />;
}

function ArticleMeta({ article, categories, ui }) {
  const categoryTitle = categories[article.category]?.title ?? ui.categoriaDefault;
  const readTime = article.readTime.replace(" minutos", "").replace(" min", "").trim();

  return (
    <p className="text-xs sm:text-sm text-gray-500">
      <span className="font-semibold text-gray-700">{categoryTitle}</span>
      <span className="mx-1.5">|</span>
      {article.date}
      <span className="mx-1.5 hidden sm:inline">|</span>
      <span className="hidden sm:inline">
        {ui.lecturaDe} {readTime} {ui.minutos}
      </span>
    </p>
  );
}

function LeerArticuloLink({ article, ui, className = "" }) {
  return (
    <Link
      to={`${BASE_PATH}/${article.category}/${article.slug}`}
      className={`inline-block text-sm font-semibold text-gray-900 underline underline-offset-4 hover:text-purple-700 transition ${className}`}
    >
      {ui.leerArticulo}
    </Link>
  );
}

export default function BlogEmpresarialHome() {
  const { lang } = useEmpresasLanguage();
  const { ui, categories, temas, articles } = useMemo(
    () => getBlogEmpresarialBundle(lang),
    [lang]
  );

  const [temaActivo, setTemaActivo] = useState(null);
  const [mostrarTodosTemas, setMostrarTodosTemas] = useState(false);

  const ordenados = useMemo(
    () => articles.slice().sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)),
    [articles]
  );

  const filtrados = useMemo(() => {
    if (!temaActivo) return ordenados;
    return ordenados.filter((a) => a.category === temaActivo);
  }, [ordenados, temaActivo]);

  const hayArticulos = filtrados.length > 0;
  const destacado = hayArticulos ? filtrados[0] : null;
  const recientes = hayArticulos ? filtrados.slice(1, 4) : [];
  const populares = hayArticulos ? ordenados.slice(0, 3) : [];
  const masTemas = hayArticulos ? filtrados.slice(0, 9) : [];

  const temasVisibles = mostrarTodosTemas ? temas : temas.slice(0, 8);
  const categoriaActiva = temaActivo ? categories[temaActivo]?.title : null;

  return (
    <div className="bg-white">
      <section className="bg-gradient-to-b from-[#e9e8f4] via-[#f5f3fc] to-white pt-8 pb-14 sm:pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block bg-purple-700 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md">
            {ui.badge}
          </span>
          <div className="mt-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
            <div className="lg:max-w-xl">
              <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-extrabold text-[#220365] leading-tight">
                {ui.heroTitle}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-purple-900/80 leading-relaxed">
                {ui.heroSubtitle}
              </p>
            </div>
            <div className="flex justify-center lg:justify-end shrink-0">
              <div className="relative p-1.5 rounded-full bg-gradient-to-br from-purple-400 via-purple-600 to-[#341d97]">
                <div className="w-44 h-44 sm:w-52 sm:h-52 rounded-full overflow-hidden border-4 border-white bg-white">
                  <img
                    src={blogEmpresarialHeroImage}
                    alt={ui.heroImageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#220365]">{ui.filterTitle}</h2>
          <p className="mt-2 text-purple-800/70">{ui.filterSubtitle}</p>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            {temasVisibles.map((tema) => {
              const Icon = ICONS[tema.icon] ?? Briefcase;
              const activo = temaActivo === tema.slug;
              return (
                <button
                  key={`${tema.label}-${tema.slug}`}
                  type="button"
                  onClick={() => setTemaActivo(activo ? null : tema.slug)}
                  className={`flex flex-col items-center justify-center gap-3 p-4 sm:p-5 rounded-2xl border bg-white shadow-sm transition hover:shadow-md min-h-[120px] ${
                    activo
                      ? "border-purple-600 ring-2 ring-purple-600/30 bg-purple-50"
                      : "border-purple-100 hover:border-purple-200"
                  }`}
                >
                  <Icon className="w-7 h-7 text-purple-800" strokeWidth={1.5} />
                  <span className="text-xs sm:text-sm font-semibold text-purple-900 leading-snug">
                    {tema.label}
                  </span>
                </button>
              );
            })}
          </div>

          {temas.length > 8 && (
            <button
              type="button"
              onClick={() => setMostrarTodosTemas((v) => !v)}
              className="mt-8 inline-flex items-center gap-2 px-6 py-2.5 rounded-full border-2 border-purple-800 text-sm font-bold text-purple-900 hover:bg-purple-800 hover:text-white transition"
            >
              {mostrarTodosTemas ? ui.verMenosTemas : ui.verTodosTemas}
              <ChevronDown
                className={`w-4 h-4 transition ${mostrarTodosTemas ? "rotate-180" : ""}`}
              />
            </button>
          )}

          {temaActivo && categoriaActiva && (
            <p className="mt-4 text-sm text-purple-700 font-medium">
              {ui.filtrando} {categoriaActiva}{" "}
              <button type="button" onClick={() => setTemaActivo(null)} className="underline ml-1">
                {ui.quitarFiltro}
              </button>
            </p>
          )}
        </div>
      </section>

      <section className="bg-[#e9e8f4] py-14 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {!hayArticulos ? (
            <div className="max-w-xl mx-auto rounded-3xl bg-white border border-purple-100 shadow-sm px-8 py-12 text-center">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#220365]">
                {temaActivo ? ui.sinArticulosFiltrado : ui.sinArticulosTitle}
              </h2>
              <p className="mt-4 text-purple-900/70 leading-relaxed">
                {ui.sinArticulosBody}
              </p>
            </div>
          ) : (
            <>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#220365] text-center mb-10">
            {ui.loUltimo}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-5">
              {destacado && (
                <article className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col sm:flex-row">
                  <Link
                    to={`${BASE_PATH}/${destacado.category}/${destacado.slug}`}
                    className="sm:w-[42%] shrink-0 block"
                  >
                    <ArticleCoverImg
                      article={destacado}
                      categories={categories}
                      className="w-full h-48 sm:h-full sm:min-h-[220px] object-cover"
                    />
                  </Link>
                  <div className="p-5 sm:p-6 flex flex-col justify-center">
                    <ArticleMeta article={destacado} categories={categories} ui={ui} />
                    <Link
                      to={`${BASE_PATH}/${destacado.category}/${destacado.slug}`}
                      className="mt-3 block"
                    >
                      <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 leading-snug hover:text-purple-700 transition">
                        {destacado.title}
                      </h3>
                    </Link>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-3">
                      {destacado.excerpt}
                    </p>
                    <LeerArticuloLink article={destacado} ui={ui} className="mt-4" />
                  </div>
                </article>
              )}

              {recientes.map((article, idx) => (
                <article
                  key={article.slug}
                  className={`rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col sm:flex-row ${
                    idx === recientes.length - 1 ? "bg-purple-100" : "bg-white"
                  }`}
                >
                  <div className="p-5 sm:p-6 flex-1 flex flex-col justify-center order-2 sm:order-1">
                    <ArticleMeta article={article} categories={categories} ui={ui} />
                    <Link
                      to={`${BASE_PATH}/${article.category}/${article.slug}`}
                      className="mt-2 block"
                    >
                      <h3 className="text-lg font-bold text-gray-900 leading-snug hover:text-purple-700 transition">
                        {article.title}
                      </h3>
                    </Link>
                    <LeerArticuloLink article={article} ui={ui} className="mt-3" />
                  </div>
                  <Link
                    to={`${BASE_PATH}/${article.category}/${article.slug}`}
                    className="sm:w-36 shrink-0 order-1 sm:order-2"
                  >
                    <ArticleCoverImg
                      article={article}
                      categories={categories}
                      className="w-full h-36 sm:h-full object-cover"
                    />
                  </Link>
                </article>
              ))}
            </div>

            <aside className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 h-fit">
              <div className="flex items-center gap-2 mb-6">
                <ThumbsUp className="w-5 h-5 text-purple-700" />
                <h3 className="text-xl font-extrabold text-gray-900">{ui.populares}</h3>
              </div>
              <ol className="space-y-6">
                {populares.map((article, idx) => (
                  <li key={article.slug} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-700 text-white text-sm font-bold">
                      {idx + 1}
                    </span>
                    <div>
                      <ArticleMeta article={article} categories={categories} ui={ui} />
                      <Link
                        to={`${BASE_PATH}/${article.category}/${article.slug}`}
                        className="mt-1 block text-sm font-bold text-gray-900 leading-snug hover:text-purple-700 transition"
                      >
                        {article.title}
                      </Link>
                    </div>
                  </li>
                ))}
              </ol>
            </aside>
          </div>

          <div className="mt-10 text-center">
            <Link
              to={temaActivo ? `${BASE_PATH}/${temaActivo}` : `${BASE_PATH}/${ordenados[0]?.category}`}
              className="inline-flex items-center justify-center px-8 py-3 rounded-full border-2 border-purple-800 text-sm font-bold text-purple-900 hover:bg-purple-800 hover:text-white transition"
            >
              {ui.verMasArticulos}
            </Link>
          </div>
            </>
          )}
        </div>
      </section>

      {hayArticulos && (
      <section className="bg-gradient-to-b from-[#ddd6fe] via-[#e9e8f4] to-[#f5f3fc] py-14 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#220365] mb-10">{ui.masTemas}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {masTemas.map((article) => (
              <article
                key={article.slug}
                className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col h-full hover:shadow-lg transition"
              >
                <Link to={`${BASE_PATH}/${article.category}/${article.slug}`}>
                  <ArticleCoverImg
                    article={article}
                    categories={categories}
                    className="w-full h-44 object-cover"
                  />
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <ArticleMeta article={article} categories={categories} ui={ui} />
                  <Link to={`${BASE_PATH}/${article.category}/${article.slug}`}>
                    <h3 className="mt-3 text-lg font-extrabold text-gray-900 leading-snug hover:text-purple-700 transition">
                      {article.title}
                    </h3>
                  </Link>
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-3 flex-1">
                    {article.excerpt}
                  </p>
                  <LeerArticuloLink article={article} ui={ui} className="mt-4" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      )}
    </div>
  );
}
